import { useState } from 'react';
import { siteInfo } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StructuredData from '@/components/feature/StructuredData';

const aboutStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: `关于我们 - ${siteInfo.name}`,
  description: `了解${siteInfo.name}的品牌故事、团队声誉与联系方式`,
  url: `${siteInfo.url}/about`,
  about: {
    '@type': 'Organization',
    name: siteInfo.name,
    description: siteInfo.description,
    url: siteInfo.url,
    foundingDate: siteInfo.foundingYear,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteInfo.phone,
      email: siteInfo.email,
      contactType: 'customer service',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '北京',
      addressCountry: 'CN',
      streetAddress: siteInfo.address,
    },
  },
};

const faqStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '网站上的资源都是免费的吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，我们收录的所有资源在基本使用层面都是免费的。部分工具和平台可能提供付费高级功能，但核心功能均可免费使用。',
      },
    },
    {
      '@type': 'Question',
      name: '如何确定推荐的资源是否可靠？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们团队的编辑人员会对每个资源进行实际测试和评估，确保推荐的资源安全、有效、可靠。',
      },
    },
    {
      '@type': 'Question',
      name: '网站多久更新一次？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们每周都会检查和更新资源列表，确保链接有效、信息准确。',
      },
    },
    {
      '@type': 'Question',
      name: '网站是否需要注册？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '不需要。第一学术福利导航网完全免费开放，无需注册即可浏览所有内容。',
      },
    },
  ],
};

const milestones = [
  { year: '2020', title: '平台创立', desc: '由一群热爱学术的研究生共同发起，旨在打破学术资源获取壁垒' },
  { year: '2021', title: '资源突破100+', desc: '收录超过100个优质学术资源，月访问用户突破1万人' },
  { year: '2022', title: '社区扩展', desc: '建立学术交流社群，用户互助分享学术资源和经验技巧' },
  { year: '2023', title: '全面升级', desc: '网站改版升级，新增分类导航和文章系统，收录资源突破500+' },
  { year: '2024', title: '持续发展', desc: '服务用户突破10万，成为国内知名的学术资源导航平台之一' },
];

const teamMembers = [
  { name: '李博文', role: '创始人 / 内容总监', desc: '北京大学信息管理博士，多年学术资源建设经验' },
  { name: '王晓琳', role: '联合创始人 / 技术负责人', desc: '清华大学计算机硕士，专注学术工具与平台开发' },
  { name: '陈思远', role: '主编', desc: '浙江大学材料科学博士，负责资源审核与内容质量控制' },
  { name: '张雨薇', role: '运营负责人', desc: '复旦大学新闻传播硕士，负责社区运营和用户服务' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('提交成功！感谢您的反馈，我们会尽快处理。');
      return;
    }
    const formData = new FormData(form);
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qb47snlsngrm4llv1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！感谢您的反馈，我们会尽快处理。');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.meta as Record<string, string>)?.detail || responseText || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(String(serverMsg));
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <div className="min-h-screen pt-16">
      <StructuredData data={{ ...aboutStructuredData }} />

      <div className="bg-background-100/70 border-b border-background-200/70">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-16">
          <Breadcrumb items={[{ label: '关于我们' }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
            关于我们
          </h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-[700px] leading-relaxed">
            了解第一学术福利导航网的品牌故事、发展历程与核心团队
          </p>
        </div>
      </div>

      <section id="brand" className="py-14 md:py-18">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14">
            <div className="flex-1">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                品牌介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                第一学术福利导航网创立于{siteInfo.foundingYear}年，是一群热爱学术研究的年轻人发起的非盈利性学术资源导航项目。我们深知科研人员在查找学术资料时面临的困难和障碍——昂贵的数据库订阅费、繁多的资源入口、真假难辨的下载渠道。因此，我们立志打造一个全面、可靠、免费的学术资源导航平台。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                我们的使命是"让每一个研究者都能平等地获取学术资源"。无论你是顶尖学府的教授，还是普通院校的学生，无论你身处一线城市还是偏远地区，第一学术福利导航网都希望成为你学术道路上的得力助手。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                截至目前，我们已经收录了<strong className="text-foreground-900">{siteInfo.stats.resources}</strong>个优质学术资源，覆盖{siteInfo.stats.categories}个主要类别，累计服务<strong className="text-foreground-900">{siteInfo.stats.users}</strong>名用户，发布了<strong className="text-foreground-900">{siteInfo.stats.articles}</strong>篇原创使用指南和工具评测文章。
              </p>
            </div>
            <div className="w-full lg:w-[400px] h-[280px] md:h-[320px] rounded-2xl overflow-hidden flex-shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20cozy%20team%20workspace%20with%20bookshelves%20filled%20with%20academic%20books%2C%20warm%20lighting%2C%20collaborative%20environment%2C%20minimalist%20design%2C%20creative%20office%20atmosphere%2C%20plants%20and%20wooden%20furniture&width=800&height=640&seq=about-brand-01&orientation=landscape"
                alt="第一学术福利导航网团队"
                className="w-full h-full object-cover"
                title="第一学术福利导航网 - 团队办公环境"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100/70 py-14 md:py-18">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
            发展历程
          </h2>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-300/60 hidden lg:block"></div>
            <div className="space-y-8 md:space-y-10">
              {milestones.map((m, idx) => (
                <div key={m.year} className={`flex flex-col lg:flex-row gap-4 lg:gap-8 items-start ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  <div className={`flex-1 ${idx % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                    <div className={`bg-background-50 border border-background-200/70 rounded-xl p-5 ${idx % 2 === 0 ? 'lg:mr-4' : 'lg:ml-4'}`}>
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-accent-100 text-accent-700 mb-2">
                        {m.year}
                      </span>
                      <h3 className="font-heading font-semibold text-foreground-900 mb-1">{m.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                  <div className="hidden lg:flex w-4 h-4 rounded-full bg-accent-500 border-4 border-background-50 flex-shrink-0 relative z-10 mt-6"></div>
                  <div className="flex-1 hidden lg:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-18">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
            核心团队
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {teamMembers.map((member) => (
              <div
                key={member.name}
                className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-user-line text-3xl text-secondary-500"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-1">{member.name}</h3>
                <p className="text-xs text-primary-600 font-medium mb-2">{member.role}</p>
                <p className="text-sm text-foreground-500 leading-relaxed">{member.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100/70 py-14 md:py-18">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10 md:gap-14">
            <div className="flex-1">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                常见问题
              </h2>
              <StructuredData data={faqStructuredData} />
              <div className="space-y-4">
                {[
                  { q: '网站上的资源都是免费的吗？', a: '是的，我们收录的所有资源在基本使用层面都是免费的。部分工具和平台可能提供付费高级功能，但核心功能均可免费使用。' },
                  { q: '如何确定推荐的资源是否可靠？', a: '我们团队的编辑人员会对每个资源进行实际测试和评估，确保推荐的资源安全、有效、可靠。' },
                  { q: '网站多久更新一次？', a: '我们每周都会检查和更新资源列表，确保链接有效、信息准确。重大更新会通过网站公告通知用户。' },
                  { q: '网站是否需要注册？', a: '不需要。第一学术福利导航网完全免费开放，无需注册即可浏览所有内容。' },
                  { q: '可以向你们推荐学术资源吗？', a: '当然可以！请通过本页底部的联系表单提交推荐，我们的编辑团队会进行审核评估。' },
                ].map((faq, idx) => (
                  <details key={idx} className="bg-background-50 border border-background-200/70 rounded-xl group cursor-pointer">
                    <summary className="px-5 py-4 font-medium text-sm text-foreground-800 flex items-center justify-between list-none">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>

            <div id="contact" className="w-full lg:w-[420px] flex-shrink-0">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                联系我们
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-800">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">联系电话</p>
                      <p className="text-sm font-medium text-foreground-800">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">地址</p>
                      <p className="text-sm font-medium text-foreground-800">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} data-readdy-form="">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-honeypot-2f7a9x"
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      提交留言
                    </button>
                  </div>

                  {formStatus !== 'idle' && (
                    <div
                      className={`mt-4 px-4 py-3 rounded-lg text-sm font-medium ${
                        formStatus === 'success'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}
                    >
                      {formMessage}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}